<template>
  <!--
    ResidentDataPrintDefault
    ─────────────────────────────────────────────────────────────────────────
    Renderiza o cabeçalho de dados do residente em telas de impressão.
    O que aparece é 100% controlado pela configuração salva em
    GET /rest-home/settings/config-resident-print-default.

    Prop obrigatória: resident_id (String | Number)

    Duas chamadas em paralelo no created():
      1. /config-resident-print-default          → configuração (quais campos mostrar)
      2. /config-resident-print-default/resident → dados do residente

    Princípios UX aplicados:
    - Nada renderiza enquanto carrega (evita flash de conteúdo incompleto)
    - Campos nulos são omitidos mesmo que estejam selected=true (evita labels vazios)
    - Labels explícitos precedem valores (escaneabilidade para profissionais de saúde)
    - Seções são separadas visualmente por espaçamento, não por bordas (reduz ruído visual em impressão)
  -->
  <div class="ml-3 rdpd-root-default">
    <!-- Estado de carregamento: silencioso, não ocupa espaço visual no print -->
    <template v-if="!loading && resident">
      <!-- ── SEÇÃO: GERAL ──────────────────────────────────────────── -->
      <template v-if="isSection('general')">
        <!-- Nome — tratado fora do loop pois tem hierarquia visual própria -->
        <h6 v-if="isField('general', 'name') && resident.name" class="mb-0">
          {{ resident.name }}
          <!-- Apelido inline ao nome, se habilitado -->
          <span
            v-if="isField('general', 'nickname') && resident.nickname"
            class="legends worksansr"
          >
            ({{ resident.nickname }})
          </span>
        </h6>

        <!-- Prontuário -->
        <h6
          v-if="
            isField('general', 'medical_record_number') &&
            resident.medical_record_number
          "
          class="legends mb-0 worksansr"
        >
          <strong>Prontuário:</strong> {{ resident.medical_record_number }}
        </h6>

        <!-- Data de nascimento + Idade -->
        <h6
          v-if="isField('general', 'date_of_birth') && resident.date_of_birth"
          class="legends mb-0 worksansr"
        >
          <strong>Nascimento:</strong>
          {{ formatDate(resident.date_of_birth, false) }} —
          {{ getAge(resident.date_of_birth) }} anos
        </h6>

        <!-- Gênero -->
        <h6
          v-if="isField('general', 'gender_id') && resident.gender_name"
          class="legends mb-0 worksansr"
        >
          <strong>Gênero:</strong> {{ resident.gender_name }}
        </h6>

        <!-- Estado civil -->
        <h6
          v-if="
            isField('general', 'marital_status_id') &&
            resident.marital_status_name
          "
          class="legends mb-0 worksansr"
        >
          <strong>Estado civil:</strong> {{ resident.marital_status_name }}
        </h6>

        <!-- Status -->
        <h6
          v-if="
            isField('general', 'status_type_id') && resident.status_type_name
          "
          class="legends mb-0 worksansr"
        >
          <strong>Status:</strong> {{ resident.status_type_name }}
        </h6>

        <!-- Telefone -->
        <h6
          v-if="isField('general', 'phone') && resident.phone"
          class="legends mb-0 worksansr"
        >
          <strong>Telefone:</strong> {{ resident.phone }}
        </h6>

        <!-- Grau de dependência -->
        <h6
          v-if="
            isField('general', 'degree_of_dependence_id') &&
            resident.degree_of_dependence_name
          "
          class="legends mb-0 worksansr"
        >
          <strong>Dependência:</strong> {{ resident.degree_of_dependence_name }}
        </h6>

        <!-- Tipo de alimentação -->
        <h6
          v-if="isField('general', 'type_of_food') && resident.type_of_food"
          class="legends mb-0 worksansr"
        >
          <strong>Alimentação:</strong> {{ resident.type_of_food }}
        </h6>

        <!-- Patologia -->
        <h6
          v-if="isField('general', 'pathology') && resident.pathology"
          class="legends mb-0 worksansr"
        >
          <strong>Patologia:</strong> {{ resident.pathology }}
        </h6>

        <!-- Alergias diversas -->
        <h6
          v-if="
            isField('general', 'miscellaneous_allergies') &&
            resident.miscellaneous_allergies
          "
          class="legends mb-0 worksansr"
        >
          <strong>Alergias:</strong> {{ resident.miscellaneous_allergies }}
        </h6>

        <!-- Naturalidade -->
        <h6
          v-if="isField('general', 'naturalness') && resident.naturalness"
          class="legends mb-0 worksansr"
        >
          <strong>Naturalidade:</strong> {{ resident.naturalness }}
        </h6>

        <!-- Religião -->
        <h6
          v-if="isField('general', 'religion') && resident.religion"
          class="legends mb-0 worksansr"
        >
          <strong>Religião:</strong> {{ resident.religion }}
        </h6>

        <!-- Profissão -->
        <h6
          v-if="isField('general', 'profession') && resident.profession"
          class="legends mb-0 worksansr"
        >
          <strong>Profissão:</strong> {{ resident.profession }}
        </h6>

        <!-- Escolaridade -->
        <h6
          v-if="isField('general', 'schooling') && resident.schooling"
          class="legends mb-0 worksansr"
        >
          <strong>Escolaridade:</strong> {{ resident.schooling }}
        </h6>

        <!-- Nome do pai -->
        <h6
          v-if="isField('general', 'fathers_name') && resident.fathers_name"
          class="legends mb-0 worksansr"
        >
          <strong>Pai:</strong> {{ resident.fathers_name }}
        </h6>

        <!-- Nome da mãe -->
        <h6
          v-if="isField('general', 'mothers_name') && resident.mothers_name"
          class="legends mb-0 worksansr"
        >
          <strong>Mãe:</strong> {{ resident.mothers_name }}
        </h6>

        <!-- Cônjuge -->
        <h6
          v-if="isField('general', 'spouse') && resident.spouse"
          class="legends mb-0 worksansr"
        >
          <strong>Cônjuge:</strong> {{ resident.spouse }}
        </h6>

        <!-- Observações -->
        <h6
          v-if="isField('general', 'comments') && resident.comments"
          class="legends mb-0 worksansr"
        >
          <strong>Obs.:</strong> {{ resident.comments }}
        </h6>

        <!-- Peso -->
        <h6
          v-if="isField('general', 'weight') && resident.weight"
          class="legends mb-0 worksansr"
        >
          <strong>Peso:</strong> {{ resident.weight }}
        </h6>

        <!-- Altura -->
        <h6
          v-if="isField('general', 'height') && resident.height"
          class="legends mb-0 worksansr"
        >
          <strong>Altura:</strong> {{ resident.height }}
        </h6>

        <!-- IMC -->
        <h6 v-if="isField('general', 'imc')" class="legends mb-0 worksansr">
          <strong>IMC:</strong>
          ({{ resident.imc.imc }}) {{ resident.imc.classificacao }}
        </h6>
      </template>

      <!-- ── SEÇÃO: HOSPEDAGEM ─────────────────────────────────────── -->
      <!--
        Hospedagem fica logo após os dados gerais pois é informação
        de alta relevância operacional para profissionais de saúde:
        leito, data de admissão e tipo de hospedagem.
      -->
      <template v-if="isSection('hosting')">
        <!-- Tipo de hospedagem -->
        <h6
          v-if="
            isField('hosting', 'type_of_hosting_id') &&
            resident.type_of_hosting_name
          "
          class="legends mb-0 worksansr"
        >
          <strong>Hospedagem:</strong> {{ resident.type_of_hosting_name }}
        </h6>

        <!-- Data de admissão -->
        <h6
          v-if="
            isField('hosting', 'date_of_reception') &&
            resident.date_of_reception
          "
          class="legends mb-0 worksansr"
        >
          <strong>Admissão:</strong>
          {{ formatDate(resident.date_of_reception, false) }}
        </h6>

        <!-- Leito — agrupado em uma linha para economizar espaço vertical -->
        <h6
          v-if="isField('hosting', 'bed_id') && resident.bed_id"
          class="legends mb-0 worksansr"
        >
          <strong>Leito Nº:</strong> {{ resident.bed_number }}
          <template v-if="resident.bed_walk"
            >, <strong>Andar:</strong> {{ resident.bed_walk }}</template
          >
          <template v-if="resident.bed_ward_or_sector"
            >, <strong>ALA/Setor:</strong>
            {{ resident.bed_ward_or_sector }}</template
          >
          <template v-if="resident.bed_bedroom"
            >, <strong>Quarto:</strong> {{ resident.bed_bedroom }}</template
          >
        </h6>

        <!-- Motivo da hospedagem -->
        <h6
          v-if="
            isField('hosting', 'reason_for_the_reception') &&
            resident.reason_for_the_reception
          "
          class="legends mb-0 worksansr"
        >
          <strong>Motivo:</strong> {{ resident.reason_for_the_reception }}
        </h6>

        <!-- Valor da mensalidade -->
        <h6
          v-if="
            isField('hosting', 'income_monthly_fee') &&
            resident.income_monthly_fee
          "
          class="legends mb-0 worksansr"
        >
          <strong>Mensalidade:</strong> {{ resident.income_monthly_fee }}
        </h6>

        <!-- Vencimento do contrato -->
        <h6
          v-if="
            isField('hosting', 'date_end_contract') &&
            resident.date_end_contract
          "
          class="legends mb-0 worksansr"
        >
          <strong>Vencimento do contrato:</strong>
          {{ formatDate(resident.date_end_contract, false) }}
        </h6>
      </template>

      <!-- ── SEÇÃO: ENDEREÇO ──────────────────────────────────────── -->
      <template v-if="isSection('address') && hasAddressData">
        <h6 class="legends mb-0 worksansr">
          <!-- Endereço composto em linha única para economizar altura no cabeçalho -->
          <template v-if="isField('address', 'address') && resident.address">
            {{ resident.address }}
          </template>
          <template
            v-if="
              isField('address', 'number_address') && resident.number_address
            "
          >
            , {{ resident.number_address }}
          </template>
          <template
            v-if="isField('address', 'complement') && resident.complement"
          >
            — {{ resident.complement }}
          </template>
          <template
            v-if="isField('address', 'neighborhood') && resident.neighborhood"
          >
            , {{ resident.neighborhood }}
          </template>
          <template v-if="isField('address', 'city') && resident.city">
            , {{ resident.city }}
          </template>
          <template v-if="isField('address', 'uf_id') && resident.uf_name">
            /{{ resident.uf_name }}
          </template>
          <template v-if="isField('address', 'cep') && resident.cep">
            — <strong>CEP:</strong> {{ resident.cep }}
          </template>
          <template
            v-if="isField('address', 'reference') && resident.reference"
          >
            — <strong>Ref.:</strong> {{ resident.reference }}
          </template>
        </h6>
      </template>

      <!-- ── SEÇÃO: DOCUMENTOS ────────────────────────────────────── -->
      <template v-if="isSection('docs')">
        <!-- CPF e RG na mesma linha quando ambos habilitados — economiza espaço vertical -->
        <h6
          v-if="
            (isField('docs', 'cpf') && resident.cpf) ||
            (isField('docs', 'rg') && resident.rg)
          "
          class="legends mb-0 worksansr"
        >
          <template v-if="isField('docs', 'cpf') && resident.cpf">
            <strong>CPF:</strong> {{ resident.cpf }}
          </template>
          <template v-if="isField('docs', 'rg') && resident.rg">
            &nbsp;| <strong>RG:</strong> {{ resident.rg }}
            <template
              v-if="isField('docs', 'issuing_body') && resident.issuing_body"
            >
              / {{ resident.issuing_body }}
            </template>
            <template
              v-if="isField('docs', 'date_of_issue') && resident.date_of_issue"
            >
              — <strong>emitido em</strong>
              {{ formatDate(resident.date_of_issue, false) }}
            </template>
          </template>
        </h6>

        <!-- Cartão SUS -->
        <h6
          v-if="isField('docs', 'SUS_card_number') && resident.SUS_card_number"
          class="legends mb-0 worksansr"
        >
          <strong>Cartão SUS:</strong> {{ resident.SUS_card_number }}
        </h6>

        <!-- Benefício INSS — agrupado para não fragmentar informação relacionada -->
        <h6 v-if="hasINSSData" class="legends mb-0 worksansr">
          <template
            v-if="
              isField('docs', 'INSS_benefit_number') &&
              resident.INSS_benefit_number
            "
          >
            <strong>INSS Nº:</strong> {{ resident.INSS_benefit_number }}
          </template>
          <template
            v-if="
              isField('docs', 'type_of_INSS_benefit') &&
              resident.type_of_INSS_benefit
            "
          >
            — <strong>Tipo:</strong> {{ resident.type_of_INSS_benefit }}
          </template>
          <template
            v-if="
              isField('docs', 'inss_benefit_status_id') &&
              resident.inss_benefit_status_name
            "
          >
            — <strong>Situação:</strong> {{ resident.inss_benefit_status_name }}
          </template>
          <template
            v-if="
              isField('docs', 'INSS_benefit_amount') &&
              resident.INSS_benefit_amount
            "
          >
            — <strong>Valor:</strong> {{ resident.INSS_benefit_amount }}
          </template>
          <template
            v-if="
              isField('docs', 'INSS_update_date') && resident.INSS_update_date
            "
          >
            — <strong>Atualizado em:</strong>
            {{ formatDate(resident.INSS_update_date, false) }}
          </template>
        </h6>
      </template>

      <!-- ── SEÇÃO: CONVÊNIOS ─────────────────────────────────────── -->
      <!--
        Convênios podem ser múltiplos (array).
        A API deve retornar resident.agreements como array de objetos.
        Se vier como objeto único, é normalizado no created().
      -->
      <template v-if="isSection('agreement') && agreements.length > 0">
        <h6
          v-for="(agreement, index) in agreements"
          :key="index"
          class="legends mb-0 worksansr"
        >
          <strong>Convênio:</strong>
          <template
            v-if="
              isField('agreement', 'covenant_name') && agreement.covenant_name
            "
          >
            {{ agreement.covenant_name }}
          </template>
          <template
            v-if="
              isField('agreement', 'type_of_agreement_id') &&
              agreement.type_of_agreement_name
            "
          >
            ({{ agreement.type_of_agreement_name }})
          </template>
          <template v-if="isField('agreement', 'number') && agreement.number">
            — <strong>Carteirinha:</strong> {{ agreement.number }}
          </template>
          <template
            v-if="
              isField('agreement', 'type_plan_name') && agreement.type_plan_name
            "
          >
            — <strong>Plano:</strong> {{ agreement.type_plan_name }}
          </template>
          <template
            v-if="
              isField('agreement', 'exchange_phone') && agreement.exchange_phone
            "
          >
            — <strong>Central:</strong> {{ agreement.exchange_phone }}
          </template>
          <template v-if="isField('agreement', 'name') && agreement.name">
            — <strong>Titular:</strong> {{ agreement.name }}
          </template>
          <template
            v-if="
              isField('agreement', 'type_of_customer_id') &&
              agreement.type_of_customer_name
            "
          >
            ({{ agreement.type_of_customer_name }})
          </template>
          <template v-if="isField('agreement', 'phone') && agreement.phone">
            — <strong>Tel:</strong> {{ agreement.phone }}
          </template>
          <template
            v-if="isField('agreement', 'cell_phone') && agreement.cell_phone"
          >
            / {{ agreement.cell_phone }}
          </template>

          <template v-if="isField('agreement', 'address') && agreement.address">
            {{ agreement.address }}
          </template>
          <template
            v-if="
              isField('agreement', 'number_address') && agreement.number_address
            "
          >
            , {{ agreement.number_address }}
          </template>
          <template
            v-if="isField('agreement', 'complement') && agreement.complement"
          >
            — {{ agreement.complement }}
          </template>
          <template
            v-if="
              isField('agreement', 'neighborhood') && agreement.neighborhood
            "
          >
            , {{ agreement.neighborhood }}
          </template>
          <template v-if="isField('agreement', 'city') && agreement.city">
            , {{ agreement.city }}
          </template>
          <template v-if="isField('agreement', 'uf_id') && agreement.uf_name">
            /{{ agreement.uf_name }}
          </template>
          <template v-if="isField('agreement', 'cep') && agreement.cep">
            — <strong>CEP:</strong> {{ agreement.cep }}
          </template>
          <template
            v-if="isField('agreement', 'reference') && agreement.reference"
          >
            — <strong>Ref.:</strong> {{ agreement.reference }}
          </template>

          <template v-if="isField('agreement', 'note') && agreement.note">
            — <strong>Obs:</strong> {{ agreement.note }}
          </template>
        </h6>
      </template>
    </template>
    <!-- /loading guard -->
  </div>
</template>

<script>
import { formatDate } from "../../lib/format";
import { getAge } from "../../lib/getAgeByDate";
import api from "../../services/api";

/**
 * Estrutura padrão espelhada do ConfigResidentPrintDefault.
 * Usada como fallback quando a API retorna null (ILPI nunca salvou configuração)
 * e como fonte da verdade de quais campos existem no merge.
 *
 * Regra: se a API retornar null → usa este DEFAULT inteiro.
 * Se a API retornar dados parciais → merge preserva selected salvo e
 * garante que campos novos adicionados aqui apareçam automaticamente.
 */
const DEFAULT_PRINT_SECTIONS = [
  {
    key: "general",
    label: "Geral",
    selected: true,
    fields: [
      { key: "name", selected: true },
      { key: "medical_record_number", selected: true },
      { key: "gender_id", selected: true },
      { key: "date_of_birth", selected: true },
      { key: "status_type_id", selected: false },
      { key: "phone", selected: false },
      { key: "degree_of_dependence_id", selected: true },
      { key: "type_of_food", selected: false },
      { key: "pathology", selected: false },
      { key: "miscellaneous_allergies", selected: false },
      { key: "nickname", selected: false },
      { key: "naturalness", selected: false },
      { key: "marital_status_id", selected: true },
      { key: "religion", selected: false },
      { key: "profession", selected: false },
      { key: "schooling", selected: false },
      { key: "fathers_name", selected: false },
      { key: "mothers_name", selected: false },
      { key: "spouse", selected: false },
      { key: "comments", selected: false },
      { key: "weight", selected: false },
      { key: "height", selected: false },
      { key: "imc", selected: false },
    ],
  },
  {
    key: "address",
    label: "Endereço",
    selected: false,
    fields: [
      { key: "cep", selected: true },
      { key: "address", selected: true },
      { key: "number_address", selected: true },
      { key: "neighborhood", selected: true },
      { key: "city", selected: true },
      { key: "uf_id", selected: true },
      { key: "complement", selected: false },
      { key: "reference", selected: false },
    ],
  },
  {
    key: "docs",
    label: "Documentos",
    selected: false,
    fields: [
      { key: "cpf", selected: true },
      { key: "rg", selected: true },
      { key: "issuing_body", selected: true },
      { key: "date_of_issue", selected: true },
      { key: "SUS_card_number", selected: true },
      { key: "INSS_benefit_number", selected: false },
      { key: "type_of_INSS_benefit", selected: false },
      { key: "inss_benefit_status_id", selected: false },
      { key: "INSS_benefit_amount", selected: false },
      { key: "INSS_update_date", selected: false },
    ],
  },
  {
    key: "agreement",
    label: "Convênios",
    selected: false,
    fields: [
      { key: "type_of_agreement_id", selected: true },
      { key: "covenant_name", selected: true },
      { key: "number", selected: true },
      { key: "type_plan_name", selected: true },
      { key: "exchange_phone", selected: true },
      { key: "name", selected: true },
      { key: "type_of_customer_id", selected: true },
      { key: "phone", selected: true },
      { key: "cell_phone", selected: false },
      { key: "note", selected: false },
      { key: "cep", selected: false },
      { key: "address", selected: false },
      { key: "number_address", selected: false },
      { key: "neighborhood", selected: false },
      { key: "city", selected: false },
      { key: "uf_id", selected: false },
      { key: "reference", selected: false },
      { key: "complement", selected: false },
    ],
  },
  {
    key: "hosting",
    label: "Hospedagem",
    selected: true,
    fields: [
      { key: "type_of_hosting_id", selected: true },
      { key: "date_of_reception", selected: true },
      { key: "bed_id", selected: true },
      { key: "income_monthly_fee", selected: false },
      { key: "reason_for_the_reception", selected: false },
      { key: "date_end_contract", selected: false },
    ],
  },
];

export default {
  name: "ResidentDataPrintDefault",

  props: {
    /**
     * ID do residente. Obrigatório.
     * Aceita String (vindo de $route.params) ou Number.
     */
    resident_id: {
      type: [String, Number],
      required: false,
    },
  },

  data() {
    return {
      loading: true,
      resident: null,

      /**
       * Mapa de configuração: { sectionKey: { selected, fields: { fieldKey: selected } } }
       * Construído a partir da resposta do endpoint de configuração.
       * Usar um mapa em vez de array permite lookup O(1) nos helpers isSection/isField.
       */
      configMap: {},
    };
  },

  computed: {
    /**
     * Normaliza convênios para sempre ser array,
     * independente de como a API retorna.
     */
    agreements() {
      if (!this.resident) return [];
      const raw = this.resident.agreements;
      if (!raw) return [];
      return Array.isArray(raw) ? raw : [raw];
    },

    /**
     * Evita renderizar uma linha de endereço vazia.
     * Verifica se ao menos um campo de endereço habilitado tem valor.
     */
    hasAddressData() {
      if (!this.resident) return false;
      const addressFields = [
        "address",
        "number_address",
        "complement",
        "neighborhood",
        "city",
        "uf_id",
        "cep",
        "reference",
      ];
      return addressFields.some(
        (key) =>
          this.isField("address", key) &&
          this.resident[key === "uf_id" ? "uf_name" : key]
      );
    },

    /**
     * Evita renderizar linha INSS vazia.
     */
    hasINSSData() {
      if (!this.resident) return false;
      const inssFields = [
        "INSS_benefit_number",
        "type_of_INSS_benefit",
        "inss_benefit_status_id",
        "INSS_benefit_amount",
        "INSS_update_date",
      ];
      return inssFields.some(
        (key) =>
          this.isField("docs", key) &&
          this.resident[
            key === "inss_benefit_status_id" ? "inss_benefit_status_name" : key
          ]
      );
    },
  },

  watch: {
    /**
     * Escuta o prop resident_id.
     * - immediate: true → comporta-se como created(), dispara na montagem
     * - handler só executa se o valor for válido (não nulo, não vazio)
     *
     * Isso resolve o caso em que o pai ainda está carregando o resident_id
     * quando o componente monta: assim que o valor chegar, init() é chamado.
     */
    resident_id: {
      immediate: true,
      handler(val) {
        if (!val) return;
        this.init();
      },
    },
  },

  methods: {
    formatDate,
    getAge,

    /**
     * Busca configuração e dados do residente em paralelo.
     * Promise.all garante que o template só renderiza quando ambos estão prontos,
     * evitando flash de conteúdo parcial — importante em telas de impressão
     * onde o usuário pode acionar Ctrl+P imediatamente.
     */
    async init() {
      this.loading = true;
      try {
        const [configRes, residentRes] = await Promise.all([
          api.get("/rest-home/settings/config-resident-print-default"),
          api.get(
            "/rest-home/settings/config-resident-print-default/resident",
            {
              params: { resident_id: this.resident_id },
            }
          ),
        ]);

        // ── Processa configuração ──────────────────────────────────
        // Se a API retornar null (ILPI nunca configurou), usa o DEFAULT inteiro.
        // Se retornar dados, faz merge: DEFAULT é a fonte dos campos disponíveis,
        // o salvo é a fonte dos valores selected — mesmo padrão do ConfigResidentPrintDefault.
        if (!configRes.data.error) {
          let saved = configRes.data.print_sections;

          if (saved) {
            // Normaliza double-encoded ("\"[...]\"" → [...])
            if (typeof saved === "string") {
              saved = JSON.parse(saved);
              if (typeof saved === "string") saved = JSON.parse(saved);
            }
            const merged = this.mergeSections(DEFAULT_PRINT_SECTIONS, saved);
            this.configMap = this.buildConfigMap(merged);
          } else {
            // null do banco → aplica o DEFAULT sem merge
            this.configMap = this.buildConfigMap(DEFAULT_PRINT_SECTIONS);
          }
        }

        // ── Processa dados do residente ────────────────────────────
        if (!residentRes.data.error) {
          this.resident = residentRes.data.resident;
        }
      } catch (e) {
        console.error("[ResidentDataPrintDefault] Erro ao inicializar:", e);
      } finally {
        this.loading = false;
      }
    },

    /**
     * Transforma o array de seções em um mapa para lookup O(1).
     *
     * Entrada (array):
     *   [{ key: "general", selected: true, fields: [{ key: "name", selected: true }] }]
     *
     * Saída (mapa):
     *   { general: { selected: true, fields: { name: true } } }
     */
    buildConfigMap(sections) {
      const map = {};
      sections.forEach((section) => {
        const fieldsMap = {};
        if (section.fields) {
          section.fields.forEach((f) => {
            fieldsMap[f.key] = f.selected;
          });
        }
        map[section.key] = {
          selected: section.selected,
          fields: fieldsMap,
        };
      });
      return map;
    },

    /**
     * Merge entre DEFAULT_PRINT_SECTIONS (fonte dos campos disponíveis)
     * e o array salvo no banco (fonte dos valores selected).
     *
     * Garante que campos novos adicionados ao DEFAULT no futuro apareçam
     * automaticamente sem quebrar configurações já salvas — idêntico ao
     * comportamento do ConfigResidentPrintDefault.
     */
    mergeSections(defaults, saved) {
      return defaults.map((defaultSection) => {
        const savedSection = saved.find((s) => s.key === defaultSection.key);
        if (!savedSection) return { ...defaultSection };

        const mergedFields = defaultSection.fields.map((defaultField) => {
          const savedField = savedSection.fields
            ? savedSection.fields.find((f) => f.key === defaultField.key)
            : null;
          return savedField
            ? { ...defaultField, selected: savedField.selected }
            : { ...defaultField };
        });

        return {
          ...defaultSection,
          selected: savedSection.selected,
          fields: mergedFields,
        };
      });
    },

    /**
     * Verifica se uma SEÇÃO inteira está habilitada na configuração.
     * Se não houver configuração salva (configMap vazio), exibe tudo por padrão.
     *
     * @param {string} sectionKey - ex: "general", "hosting"
     */
    isSection(sectionKey) {
      if (!this.configMap[sectionKey]) return true; // sem config → exibe
      return this.configMap[sectionKey].selected === true;
    },

    /**
     * Verifica se um CAMPO específico está habilitado na configuração.
     * A seção pai precisa estar habilitada (isSection) para que qualquer campo apareça.
     *
     * @param {string} sectionKey - ex: "general"
     * @param {string} fieldKey   - ex: "name"
     */
    isField(sectionKey, fieldKey) {
      if (!this.isSection(sectionKey)) return false;
      const section = this.configMap[sectionKey];
      if (!section || section.fields[fieldKey] === undefined) return true; // sem config → exibe
      return section.fields[fieldKey] === true;
    },
  },
};
</script>

<style>
/*
  Escopo local com prefixo .rdpd- para não colidir com estilos globais.
  CSS mínimo — o visual já é tratado pelas classes utilitárias do projeto
  (legends, worksansr, mb-0).
*/
.rdpd-root-default {
  max-width: 400px;
  display: flex;
  flex-direction: column;
  justify-content: center;
}
</style>